import React from "react";
import Head from "next/head";
import PricingPage from "../../../../../src/modules/PricingPage/PricingPage";
import { newVehicles, vehicles } from "../../../../../src/constants/vehicleData";

// Allowed cities list
const ALLOWED_CITIES = [
  "pune",
  "mumbai",
  "bangalore",
  "hyderabad",
  "chennai",
  "kolkata",
  "delhi-ncr",
  "jaipur",
  "lucknow",
  "ahmedabad",
  "indore",
];

// Helper function to format city name (e.g., "bangalore" -> "Bangalore", "delhi-ncr" -> "Delhi-NCR")
const formatCityName = (city) => {
  if (!city) return "";
  // Special handling for Delhi-NCR
  if (city.toLowerCase() === "delhi-ncr") {
    return "Delhi-NCR";
  }
  return city
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Helper function to get state from city
const getStateFromCity = (city) => {
  const cityStateMap = {
    pune: "Maharashtra",
    mumbai: "Maharashtra",
    bangalore: "Karnataka",
    hyderabad: "Telangana",
    chennai: "Tamil Nadu",
    kolkata: "West Bengal",
    "delhi-ncr": "Delhi",
    jaipur: "Rajasthan",
    lucknow: "Uttar Pradesh",
    ahmedabad: "Gujarat",
    indore: "Madhya Pradesh",
  };
  return cityStateMap[city?.toLowerCase()] || "India";
};

/** Build all valid (brand, model) pairs for static generation */
function getStaticParamSegments() {
  const vehicleData = [...vehicles, ...newVehicles];
  const seen = new Set();
  const segments = [];
  vehicleData.forEach((v) => {
    const brand = v?.brand;
    const model = v?.vehicle_path_id;
    if (!brand || !model) return;
    const key = `${brand}|${model}`;
    if (seen.has(key)) return;
    seen.add(key);
    segments.push({ brand, model });
  });
  return segments;
}

/** Shared data for this route (used by getStaticProps and the page). */
function getPageData(brand, model, city) {
  const cityLower = city?.toLowerCase();
  if (!cityLower || !ALLOWED_CITIES.includes(cityLower)) {
    return null;
  }
  const vehicleData = [...vehicles, ...newVehicles];
  const vehicleDataLocal = vehicleData?.filter(
    (vehicle) => vehicle?.vehicle_path_id?.toLowerCase() === model?.toLowerCase()
  );
  if (!vehicleDataLocal?.length) return null;
  const latestVehiclesInfo = vehicleData?.filter(
    (vehicle) => vehicle?.vehicle_path_id?.toLowerCase() !== model?.toLowerCase()
  );
  const formattedCity = formatCityName(city);
  const state = getStateFromCity(city);
  const location = `${formattedCity}, ${state}`;
  return {
    brand: brand || "",
    model: model || "",
    city: city || "",
    vehicleDataLocal: vehicleDataLocal || [],
    latestVehiclesInfo: latestVehiclesInfo || [],
    location,
    availableCities: ALLOWED_CITIES,
  };
}

export async function getStaticPaths() {
  const segments = getStaticParamSegments();
  const paths = [];
  for (const { brand, model } of segments) {
    for (const city of ALLOWED_CITIES) {
      paths.push({ params: { brand, model, city } });
    }
  }
  return { paths, fallback: "blocking" };
}

export async function getStaticProps(context) {
  const { brand, model, city } = context.params;
  const data = getPageData(brand, model, city);
  if (!data) return { notFound: true };
  return { props: data };
}

export default function DynamicPricingPage({
  brand,
  model,
  city,
  vehicleDataLocal = [],
  latestVehiclesInfo = [],
  location = "",
  availableCities = [],
}) {
  const formattedCity = formatCityName(city);
  const state = getStateFromCity(city);
  const cityState = `${formattedCity}, ${state}`;
  const vehicleName = vehicleDataLocal?.[0]?.vehicle_name || model;
  const vehicleBrand = vehicleDataLocal?.[0]?.brand || brand;

  // LCP image preload (first gallery image) for faster LCP
  const firstProduct = vehicleDataLocal?.[0]?.ProductImages?.[0];
  const lcpImageDesktop = firstProduct?.imagesDesktop?.[0]?.src;
  const lcpImageMobile = firstProduct?.imagesMobile?.[0]?.src;

  // Generate dynamic breadcrumbs
  const BREADCRUMB_ITEMS = [
    { label: "Home", href: "/" },
    {
      label: "Electric Commercial Vehicles",
      href: "/electric-commercial-vehicles",
    },
    {
      label: vehicleBrand,
      href: `/electric-commercial-vehicles/${brand}`,
    },
    {
      label: vehicleName,
      href: `/electric-commercial-vehicles/${brand}/${model}`,
    },
    {
      label: `${vehicleName} Price in ${formattedCity}`,
    },
  ];

  // Generate dynamic breadcrumb schema
  const BREADCRUMB_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.turno.club/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Electric Commercial Vehicles",
        item: "https://www.turno.club/electric-commercial-vehicles",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: vehicleBrand,
        item: `https://www.turno.club/electric-commercial-vehicles/${brand}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: vehicleName,
        item: `https://www.turno.club/electric-commercial-vehicles/${brand}/${model}`,
      },
      {
        "@type": "ListItem",
        position: 5,
        name: `${vehicleName} Price in ${formattedCity}`,
        item: `https://www.turno.club/electric-commercial-vehicles/${brand}/${model}/price/${city}`,
      },
    ],
  };

  // Generate dynamic meta content
  const H1_HEADING = `${vehicleBrand} ${vehicleName} Price in ${cityState}`;
  const title = `${vehicleBrand} ${vehicleName} Price in ${formattedCity} - Get ${vehicleName} On Road Price in 2026`;
  const description = `Check the latest ${vehicleBrand} ${vehicleName} price in ${formattedCity}. Get accurate ${vehicleName} on road price in ${formattedCity}, variants, EMI options, and updated offers on electric autos.`;
  const canonicalUrl = `https://www.turno.club/electric-commercial-vehicles/${brand}/${model}/price/${city}`;

  const META_CONTENT = [
    {
      name: "description",
      content: description,
    },
    {
      name: "robots",
      content: "INDEX, FOLLOW",
    },
    {
      name: "keywords",
      content: `${vehicleName} price ${formattedCity}, ${vehicleBrand} ${vehicleName} price, electric auto price ${formattedCity}, commercial vehicle price, EV pricing`,
    },
    {
      property: "og:title",
      content: title,
    },
    {
      property: "og:description",
      content: description,
    },
    {
      property: "og:url",
      content: canonicalUrl,
    },
  ];

  // Get vehicle name for key specifications (normalize to match keySpecificationsInfo keys)
  const vehicleNameKey = vehicleDataLocal?.[0]?.name
    ?.replace(/\s+/g, "")
    .replace(/-/g, "");

  return (
    <>
      {lcpImageDesktop && (
        <Head>
          <link
            rel="preload"
            as="image"
            href={lcpImageDesktop}
            media="(min-width: 769px)"
          />
        </Head>
      )}
      {lcpImageMobile && (
        <Head>
          <link
            rel="preload"
            as="image"
            href={lcpImageMobile}
            media="(max-width: 768px)"
          />
        </Head>
      )}
      <PricingPage
      title={title}
      metaContent={META_CONTENT}
      canonicalUrl={canonicalUrl}
      BREADCRUMB_ITEMS={BREADCRUMB_ITEMS}
      BREADCRUMB_SCHEMA={BREADCRUMB_SCHEMA}
      H1_HEADING={H1_HEADING}
      location={location || cityState}
      vehicleDataLocal={vehicleDataLocal}
      KEY_SPECIFICATION_HEADER="Key Specifications"
      vehicleName={vehicleNameKey}
      make={vehicleBrand}
      model={vehicleName}
      minPrice={
        vehicleDataLocal?.[0]?.priceRange?.default?.minPrice
      }
      maxPrice={
        vehicleDataLocal?.[0]?.priceRange?.default?.maxPrice
      }
      gvw={vehicleDataLocal?.[0]?.gvw}
      payload={vehicleDataLocal?.[0]?.load || vehicleDataLocal?.[0]?.payload}
      range={vehicleDataLocal?.[0]?.range}
      brand={brand}
      modelSlug={model}
      citySlug={city}
      availableCities={availableCities}
      latestVehiclesInfo={latestVehiclesInfo}
      LATEST_VEHICLES_HEADER="Similar Electric 3 Wheelers"
    />
    </>
  );
}

